import {
  createBlock,
  mergeBlock,
  NodeUtils,
  t,
} from "@easy-email-pro-core/utils";

import { ElementCategory, StandardType } from "@easy-email-pro-core/constants";
import {
  ElementDefinition,
  StandardButtonElement,
} from "@easy-email-pro-core/typings";
import React from "react";
import { Button } from "@easy-email-pro-core/components/Elements";

import { Button as SourceElement } from "../../basic/Button";
import { BlockRenderer, ContentLeaf } from "@easy-email-pro-core/components";

const defaultData: ElementDefinition<StandardButtonElement>["defaultData"] =
  mergeBlock(SourceElement.defaultData, {
    attributes: {
      "inner-padding-top": "10px",
      "inner-padding-bottom": "10px",
      "inner-padding-left": "25px",
      "inner-padding-right": "25px",
      "border-width": "1px",
      "border-style": "solid",
      "border-color": "#AAAAAA",
      "text-decoration": "none",
      "text-transform": "none",
      "vertical-align": "middle",
    },
    data: {},
  });

export const StandardButton = createBlock<StandardButtonElement>({
  get name() {
    return t("Button");
  },
  defaultData,
  type: StandardType.STANDARD_BUTTON,
  create: (payload) => {
    const defaultData: StandardButtonElement = {
      type: StandardType.STANDARD_BUTTON,
      data: {
        content: "Button",
      },
      attributes: {},
      children: [
        {
          text: t("Click"),
        },
      ],
    };
    return mergeBlock(defaultData, payload);
  },
  category: ElementCategory.BUTTON,
  render(params) {
    const { node } = params;

    const {
      "inner-padding-top": innerPaddingTop,
      "inner-padding-bottom": innerPaddingBottom,
      "inner-padding-left": innerPaddingLeft,
      "inner-padding-right": innerPaddingRight,
      "border-enabled": borderEnabled,
      "border-width": borderWidth,
      "border-style": borderStyle,
      "border-color": borderColor,
      "icon-enabled": iconEnabled,
      "icon-position": iconPosition,
      "icon-width": iconWidth,
      "icon-height": iconHeight,
      "icon-src": iconSrc,
      "icon-vertical-align": iconVerticalAlign,
      ...attributes
    } = node.attributes;

    const top = innerPaddingTop || defaultData.attributes["inner-padding-top"];
    const right =
      innerPaddingRight || defaultData.attributes["inner-padding-right"];
    const bottom =
      innerPaddingBottom || defaultData.attributes["inner-padding-bottom"];
    const left =
      innerPaddingLeft || defaultData.attributes["inner-padding-left"];
    const innerPadding = `${top} ${right} ${bottom} ${left}`;

    const border = borderEnabled
      ? `${borderWidth || "1px"} ${borderStyle || "solid"} ${
          borderColor || "#AAAAAA"
        }`
      : "";

    if (attributes.direction === "rtl") {
      if (!attributes["css-class"]) {
        attributes["css-class"] = "";
      }
      attributes["css-class"] = attributes["css-class"] + " direction-rtl";
    }
    if (iconEnabled && iconSrc) {
      return (
        <Button
          idx={params.idx}
          {...attributes}
          border={border}
          inner-padding={innerPadding}
          data={node.data}
          children={
            <>
              {iconPosition === "left" && (
                <>
                  {`<img
                    src='${iconSrc}'
                    width='${iconWidth?.replace("px", "")}'
                    height='${iconHeight?.replace("px", "")}'
                    style='width: ${iconWidth}; height: ${iconHeight}; display: inline-block; vertical-align: ${iconVerticalAlign};'
                  />&nbsp;`}
                </>
              )}

              {node.children.map((child, index) => {
                if (NodeUtils.isTextNode(child)) {
                  return (
                    <ContentLeaf
                      key={index}
                      {...params}
                      node={{ ...child }}
                      extraStyle={
                        iconVerticalAlign
                          ? { verticalAlign: iconVerticalAlign }
                          : undefined
                      }
                    />
                  );
                }
                return <BlockRenderer key={index} {...params} node={child} />;
              })}

              {iconPosition === "right" && (
                <>
                  {`&nbsp;<img
                    src='${iconSrc}'
                    width='${iconWidth?.replace("px", "")}'
                    height='${iconHeight?.replace("px", "")}'
                    style='width: ${iconWidth}; height: ${iconHeight}; display: inline-block; vertical-align: ${iconVerticalAlign};'
                  />`}
                </>
              )}
            </>
          }
        />
      );
    }
    return (
      <Button
        idx={params.idx}
        {...attributes}
        border={border}
        inner-padding={innerPadding}
        data={node.data}
        children={params.children || node.children}
      />
    );
  },
});
